import React, { useEffect } from 'react'
import { useListState } from '@mantine/hooks'
import { toLower } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { TimelineProps } from '@node-space/ui-components'
import { PaymentsResponse, PaymentStatus, PaymentType } from 'types/payments'
import { calculatePercentageDiff } from 'utils/utils'
import { PaymentTimelineRowInfo } from '../components/Table/Timeline/PaymentTimelineRowInfo'

export const useUpdateTimeline = (transaction: PaymentsResponse) => {
  const { t } = useTranslation()
  const isPaymentIn = transaction?.type === PaymentType.IN
  const amountDue = transaction?.walletCurrency
    ? transaction.paidCurrency?.amount - transaction.paidCurrency?.actual
    : 0

  const [values, handlers] = useListState<TimelineProps['items'][number]>([])

  useEffect(() => {
    const canMarkPaymentComplete = transaction?.status === PaymentStatus.PROCESSING
    const isUnderpaidStatus = transaction?.status === PaymentStatus.UNDERPAID
    const isOverpaidStatus = transaction?.status === PaymentStatus.OVERPAID
    const hasMultipleTransactions =
      transaction?.transactions &&
      transaction.transactions.length > 1 &&
      !isUnderpaidStatus &&
      !isOverpaidStatus
    const isQuoteAccepted =
      transaction?.quoteStatus !== 'TEMPLATE' && transaction?.quoteStatus !== 'PENDING'

    const createTransactionRow = (
      current: PaymentsResponse['transactions'][number],
      variant: 'info' | 'success' = 'info'
    ): TimelineProps['items'][number] => ({
      variant,
      date: new Date(current?.dateConfirmed),
      Title: (
        <PaymentTimelineRowInfo
          generalInfo={t('paymentOfTimeline', {
            action: isPaymentIn ? t('payments.received') : t('payments.sent'),
          })}
          testid="timeline-action-of-value"
          boldInfo={`${current?.amount} ${transaction?.paidCurrency?.currency}`}
        />
      ),
    })

    const getInitialRow = (): TimelineProps['items'][number] => ({
      variant: 'info',
      date: new Date(transaction?.dateCreated),
      Title: (
        <PaymentTimelineRowInfo
          generalInfo={t('newPaymentInfoTimeline', {
            paymentType: isPaymentIn ? t('payments.in') : t('payments.out'),
          })}
          boldInfo={`${transaction?.displayCurrency?.amount} ${transaction?.displayCurrency?.currency}`}
          testid="timeline-action-of-value"
        />
      ),
    })

    const getQuoteAcceptedRow = (): TimelineProps['items'][number] | null => {
      if (!isQuoteAccepted) return null

      return {
        variant: 'info',
        Title: (
          <PaymentTimelineRowInfo
            generalInfo={t('customerAcceptedQuoteTimeline', {
              action: isPaymentIn
                ? t('transactionAction.pay').toLowerCase()
                : t('transactionAction.receive').toLowerCase(),
            })}
            boldInfo={`${transaction?.paidCurrency?.amount} ${transaction?.paidCurrency?.currency}`}
          />
        ),
      }
    }

    const getMultipleTransactionRows = (): TimelineProps['items'] => {
      if (!hasMultipleTransactions) return []
      return transaction.transactions.map(current => createTransactionRow(current))
    }

    const getStatusRows = (): TimelineProps['items'] => {
      switch (transaction?.status) {
        case PaymentStatus.EXPIRED: {
          const isExpired = transaction?.expiryDate <= Date.now()
          return [
            ...(isExpired
              ? [
                  {
                    date: new Date(transaction?.expiryDate),
                    variant: 'error' as const,
                    Title: <PaymentTimelineRowInfo generalInfo={t('paymentExpired')} />,
                  },
                ]
              : []),
            ...(transaction?.latePayment
              ? [
                  {
                    variant: 'error' as const,
                    Title: (
                      <PaymentTimelineRowInfo
                        generalInfo={t('latePaymentDescription', {
                          paidAmount: transaction?.paidCurrency?.actual,
                          paidCurrency: transaction?.paidCurrency?.currency,
                        })}
                      />
                    ),
                  },
                ]
              : []),
          ]
        }

        case PaymentStatus.UNDERPAID: {
          const transactionRows =
            transaction?.transactions?.map(current => createTransactionRow(current)) ?? []
          const percentageRemaining = canMarkPaymentComplete
            ? ` (${calculatePercentageDiff(
                transaction?.paidCurrency?.amount,
                transaction?.paidCurrency?.actual,
                'remaining'
              )} ${toLower(t('owing'))})`
            : ''

          return [
            ...transactionRows,
            {
              variant: 'warning' as const,
              Title: isPaymentIn ? (
                <PaymentTimelineRowInfo
                  generalInfo={t('amountDue')}
                  testid="timeline-amount-due-value"
                  boldInfo={`${amountDue} ${transaction?.paidCurrency?.currency}${percentageRemaining}`}
                />
              ) : (
                <PaymentTimelineRowInfo generalInfo={t('paymentIsProcessing')} />
              ),
            },
          ]
        }

        case PaymentStatus.OVERPAID:
          return transaction?.transactions?.map(current => createTransactionRow(current)) ?? []

        case PaymentStatus.COMPLETE: {
          const latestTransaction = transaction?.transactions?.reduce((latest, current) => {
            return new Date(current?.dateConfirmed) > new Date(latest?.dateConfirmed)
              ? current
              : latest
          }, transaction?.transactions?.[0])

          const percentagePaid = canMarkPaymentComplete
            ? ` (${calculatePercentageDiff(
                transaction?.paidCurrency?.amount,
                transaction?.paidCurrency?.actual,
                'paid'
              )} ${toLower(t('paid'))})`
            : ''

          return [
            {
              variant: 'success' as const,
              date: new Date(latestTransaction?.dateConfirmed),
              Title: (
                <PaymentTimelineRowInfo
                  generalInfo={t('paymentOfTimeline', {
                    action: isPaymentIn ? t('payments.received') : t('payments.sent'),
                  })}
                  testid="timeline-action-of-value"
                  boldInfo={`${transaction?.paidCurrency?.actual} ${transaction?.paidCurrency?.currency}${percentagePaid}`}
                />
              ),
            },
          ]
        }

        default:
          return []
      }
    }

    const getEnhancedChecksRow = (): TimelineProps['items'][number] | null => {
      if (!transaction?.hasEnhancedChecks) return null

      return {
        variant: 'warning',
        Title: <PaymentTimelineRowInfo generalInfo={t('payments.statusOnHoldTimeline')} />,
      }
    }

    const updatedTimelineRows = [
      getInitialRow(),
      getQuoteAcceptedRow(),
      ...getMultipleTransactionRows(),
      ...getStatusRows(),
      getEnhancedChecksRow(),
    ]
      .filter((row): row is TimelineProps['items'][number] => row !== null)
      .sort((firstRow, secondRow) => {
        return new Date(firstRow.date).getTime() - new Date(secondRow.date).getTime()
      })

    handlers.setState(updatedTimelineRows)
  }, [transaction])

  return values
}
